import { Sector } from 'recharts'
import { Button, Modal, Image, Tooltip as TableTooltip } from 'antd'
import React, { FunctionComponent, useState } from 'react'
import {
  DownloadOutlined,
  FileImageOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { ResultSet } from '@cubejs-client/core'
import { CSVLink } from 'react-csv'
import './ChartRenderer.scss'
import { useDispatch } from 'react-redux'
import { getAnprImageUrl } from '../../redux/actions/scenes'
import FileSaver, { saveAs } from 'file-saver'
import JSZip from 'jszip'
import moment from 'moment/moment'

// https://recharts.org/en-US/examples/CustomActiveShapePieChart
export function renderActiveShape(props: any) {
  const RADIAN = Math.PI / 180
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value
  } = props
  const sin = Math.sin(-RADIAN * midAngle)
  const cos = Math.cos(-RADIAN * midAngle)
  const sx = cx + (outerRadius + 10) * cos
  const sy = cy + (outerRadius + 10) * sin
  const mx = cx + (outerRadius + 30) * cos
  const my = cy + (outerRadius + 30) * sin
  const ex = mx + (cos >= 0 ? 1 : -1) * 22
  const ey = my
  const textAnchor = cos >= 0 ? 'start' : 'end'

  return (
    <g>
      <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
        {payload.name}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
      >{`${value} ${props.description}`}</text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#999"
      >
        {`(${(percent * 100).toFixed(0)}%)`}
      </text>
    </g>
  )
}

type CSVExporttProps = {
  resultSet: ResultSet
}

export const CSVExport: FunctionComponent<CSVExporttProps> = ({
  resultSet
}) => {
  const { t } = useTranslation(['datadiscovery'])
  return (
    <CSVLink
      data={resultSet.tablePivot()}
      filename={'export.csv'}
      className="scc--datadiscovery--widget-exportbutton"
    >
      <Button type="primary" style={{}}>
        <DownloadOutlined />
        {t('widgets.export')}
      </Button>
    </CSVLink>
  )
}

type AnprSampleExportProps = {
  resultSet: ResultSet
  widgetTitle: string
}

export const AnprSampleExport: FunctionComponent<AnprSampleExportProps> = ({
  resultSet,
  widgetTitle
}) => {
  const { t } = useTranslation(['datadiscovery'])
  const dispatch: any = useDispatch()
  const imageNameColumn = 'ParkingAnalyticsEvents.numberPlate'

  const [loading, setLoading] = useState<boolean>(false)

  const anprColumns = getAnprImageColumns(
    Object.keys(resultSet.annotation().dimensions)
  )

  const downloadAllImages = async () => {
    setLoading(true)
    let imageIds: string[] = []
    let imageNames: string[] = []

    resultSet.tablePivot().forEach((event) => {
      anprColumns.forEach((column) => {
        let imageId = event[column].toString()
        imageIds.push(imageId)

        // get file name
        let fileNameParts = imageId.split('/')
        let originalFileName = fileNameParts[fileNameParts.length - 1]
        let filenameExtension = originalFileName.split('.').pop()

        if (Object.keys(event).includes(imageNameColumn)) {
          let fileName = event[imageNameColumn].toString()
          let fullFileName = fileName.concat(`.${filenameExtension}`)

          // check if filename already exists
          let occurrences = imageNames.filter((item) => {
            return item === fullFileName
          }).length
          if (occurrences > 0) {
            fullFileName = fileName.concat(
              `_${occurrences}.${filenameExtension}`
            )
          }
          imageNames.push(fullFileName)
        } else {
          imageNames.push(originalFileName)
        }
      })
    })

    let data = await dispatch(getAnprImageUrl(imageIds))
    let downloadUris: string[] = []
    data.response.forEach((item) => {
      downloadUris.push(item.downloadURI)
    })

    // download all images via zip
    // stackoverflow.com/questions/58143424/react-tsx-download-multiple-image-file-in-zip-format
    // https://jsfiddle.net/jaitsujin/zrdgsjht/
    let zip = new JSZip()
    let zipFilename = widgetTitle
      ? widgetTitle
      : t('widgets.showAnprImage.defaultZipName')
    zipFilename = zipFilename.toLowerCase().replaceAll(' ', '-')
    let zipFilenameFull = `${zipFilename}_${Date.now()}.zip`
    downloadUris.forEach(function (url, i) {
      let blob = fetch(url).then((r) => r.blob())
      zip.file(imageNames[i], blob, { binary: true })
    })
    zip
      .generateAsync({ type: 'blob' })
      .then(function (content) {
        FileSaver.saveAs(content, zipFilenameFull)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return anprColumns.length ? (
    <Button
      type="primary"
      className="scc--datadiscovery--widget-exportbutton"
      onClick={downloadAllImages}
      disabled={loading}
    >
      {loading ? <LoadingOutlined /> : <FileImageOutlined />}
      {t('widgets.showAnprImage.export')}
    </Button>
  ) : (
    <></>
  )
}

type ShowAnprImageprops = {
  imageId: string
}

const ShowAnprImage: FunctionComponent<ShowAnprImageprops> = (props) => {
  const { t } = useTranslation(['datadiscovery'])
  const dispatch: any = useDispatch()

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined)

  const showImage = () => {
    dispatch(getAnprImageUrl([props.imageId])).then((data) => {
      setImageUrl(data.response[0].downloadURI)
      setIsModalVisible(true)
    })
  }
  return (
    <>
      <Button size="small" onClick={showImage} icon={<FileImageOutlined />}>
        {t('widgets.showAnprImage.show')}
      </Button>
      <Modal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
        }}
        footer={[
          imageUrl && !showError && (
            <Button
              key="submit"
              type="primary"
              onClick={() => {
                // get file name
                let fileNameParts = props.imageId.split('/')
                let originalFileName = fileNameParts[fileNameParts.length - 1]
                saveAs(`${imageUrl}`, originalFileName)
              }}
            >
              {t('widgets.showAnprImage.download')}
            </Button>
          ),
          <Button
            key="back"
            onClick={() => {
              setIsModalVisible(false)
            }}
          >
            {t('widgets.showAnprImage.close')}
          </Button>
        ]}
      >
        <div className="scc--datadiscovery--widget-anprImage">
          <Image
            preview={false}
            src={imageUrl}
            onError={() => {
              setShowError(true)
            }}
            fallback="data:image/png;base64,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"
          />
          {showError && (
            <p className={'scc--datadiscovery--widget-anprImage-error'}>
              {t('widgets.showAnprImage.error')}
            </p>
          )}
        </div>
      </Modal>
    </>
  )
}

// table columns renderer
const renderTableColumnTooltip = (column) => {
  return (
    <TableTooltip placement="topLeft" title={column}>
      {column}
    </TableTooltip>
  )
}

const getAnprImageColumns = (columns: string[]) => {
  return columns.filter((item) => {
    return (
      item === 'ParkingAnalyticsEvents.entryImage' ||
      item === 'ParkingAnalyticsEvents.exitImage'
    )
  })
}

const renderAnprImageButton = (column) => {
  return <ShowAnprImage imageId={column} />
}

const parseDuration = (durationString) => {
  let split = durationString.split(' ')
  let days = parseInt(split[0].replace('d', ''))
  let hours = parseInt(split[1].replace('h', ''))
  let minutes = parseInt(split[2].replace('m', ''))
  return (days * 24 + hours) * 60 + minutes
}

const parseTime = (timeString) => {
  timeString = timeString.replace('\u00a0(UTC)', '')
  if (timeString.indexOf('W')) {
    // week cannot be parsed automatically, we have to split manually
    timeString = timeString.split('-W')
    return moment(timeString[0]).week(parseInt(timeString[1])).valueOf()
  }
  return moment(timeString).valueOf()
}

const parseSpeed = (speedWindowString) => {
  let split = speedWindowString.split('-')
  let beginSpeedWindow = parseInt(split[0])
  return isNaN(beginSpeedWindow) ? Number.MAX_SAFE_INTEGER : beginSpeedWindow
}

const getColumnSorter = (key) => {
  return (a, b) => {
    let first = a[key]
    let second = b[key]

    if (~key.indexOf('speed') || ~key.indexOf('Speed')) {
      // calculate Speed for sorting
      first = parseSpeed(a[key])
      second = parseSpeed(b[key])
    }
    if (~key.indexOf('duration') || ~key.indexOf('Duration')) {
      // calculate duration for sorting
      first = parseDuration(a[key])
      second = parseDuration(b[key])
    }
    if (~key.indexOf('timestamp') || ~key.indexOf('Time')) {
      // calculate time for sorting
      first = parseTime(a[key])
      second = parseTime(b[key])
    }

    if (typeof first == 'number') {
      return first - second
    }
    if (typeof first == 'string') {
      if (first < second) {
        return -1
      }
      if (first > second) {
        return 1
      }
    }
    return 0
  }
}

export function addCustomRenderMethods(column) {
  if (getAnprImageColumns([column.key]).length) {
    column.render = renderAnprImageButton
  } else {
    column.render = renderTableColumnTooltip
  }

  column.sorter = getColumnSorter(column.key)
  return column
}
